import { firebaseShared } from "@shared/firebase.js";
import * as firebase from "@shared/firebase.js";
import * as environment from "@shared/environment.js";
import { dlog } from "@shared/utility.js";
import * as imageCache from "@shared/imageCache.js";

let allColors = [];

function colorWithID(id) {
    return allColors.find(item => item.id === id);
}

function getAllColors() {
    return allColors ? [...allColors] : [];
}

async function fetchColors(force = false) {
    // Due to some weird firebase bug(?) where the auth token times out (I think in 30m), let's avoid trying to re-fetch feeds from firebase if we already have them.
    if (!force && allColors.length > 0) {
        dlog("already fetched colors. bailing.");
        return;
    }

    try {
        dlog(`fetchColors: start; collection: ${environment.currentColorCollectionName()}`);

        const collectionRef = firebase.collection(firebaseShared.db, environment.currentColorCollectionName());
        dlog(`fetchColors: got collectionRef`);
        
        const snapshot = await firebaseShared.withRetry(() => firebase.getDocs(collectionRef));
        dlog(`fetchColors: got snapshot`);

        const tempColors = [];

        snapshot.forEach(doc => {
            tempColors.push(doc.data());
        });
        
        allColors = tempColors;
        dlog("allColors count: " + allColors.length);
    } catch (error) {
        console.warn("fetchColors failed after retries:", error);
        return;
    }

    dlog(`fetchColors: ${allColors.length} items in collection`);
}

function generateColorKey(primaryColor, altColor) {
    if (!primaryColor) {
        return null;
    }

    if (altColor) {
        return `${primaryColor.id}-${altColor.id}`;
    } else {
        return `${primaryColor.id}-none`;
    }
}

function getProductImageID(productSKU) {
    return productSKU?.imageID || null;
}

function getISOPreviewImageID(productSKU) {
    if (!productSKU) return null;
    if (productSKU.productCategory === "wallet") return productSKU.isoImageRightID || null;
    if (productSKU.productCategory === "grip") return productSKU.isoImageUpID || null;
    return null;
}

function getOverlayImageID(productSKU) {
    if (!productSKU) return null;
    return productSKU.overlayID || null;
}

// Pre-load product images for sibling ProductSKUs (new ProductSKU model)
// This preloads the main product image for each SKU to enable fast color switching
async function preloadSiblingSKUImages(siblingSKUs) {
    if (!siblingSKUs || siblingSKUs.length === 0) {
        dlog("[COLOR VARIANTS] No sibling SKUs provided for preloading");
        return;
    }

    const imagesToPreload = siblingSKUs
        .filter(sku => sku?.imageID)
        .map(sku => ({ skuID: sku.id, imageID: sku.imageID }));

    if (imagesToPreload.length === 0) {
        dlog("[COLOR VARIANTS] No images to preload from sibling SKUs");
        return;
    }

    dlog(`[COLOR VARIANTS] Pre-loading ${imagesToPreload.length} product images for sibling SKUs`);

    const maxPrdImageW = 400;
    const dpr = Math.round(window.devicePixelRatio);

    const preloadPromises = imagesToPreload.map(async ({ skuID, imageID }) => {
        try {
            await imageCache.getLoadAndStoreCF(imageID, maxPrdImageW, "webp", 85, dpr);
            dlog(`[COLOR VARIANTS] Pre-loaded image for SKU: ${skuID}`);
        } catch (error) {
            dlog(`[COLOR VARIANTS] Failed to pre-load image for SKU: ${skuID}, error: ${error.message}`);
        }
    });

    try {
        await Promise.all(preloadPromises);
        dlog("[COLOR VARIANTS] Completed pre-loading sibling SKU images");
    } catch (error) {
        dlog(`[COLOR VARIANTS] Some sibling SKU images failed to pre-load: ${error.message}`);
    }
}

export { colorWithID, fetchColors, getAllColors, generateColorKey, getProductImageID, getISOPreviewImageID, getOverlayImageID, preloadSiblingSKUImages };

/*
Color schema for reference:

**Colors** represent the color options available for customization. Each color has a unique identifier, display name, and 8-character hex color value including alpha channel for accurate color representation across the platform.

| **Field**       | **Type** | **Description**                                              |
| --------------- | -------- | ------------------------------------------------------------ |
| `id`            | string   | Unique identifier for the color (e.g., "black1", "white1").  |
| `title`         | string   | User-visible display name of the color (e.g., "Black", "White"). |
| `hex8Color`     | string   | 8-character hex color value including alpha channel (e.g., "#000000FF"). |
| `last_modified` | string   | ISO-8601 timestamp of the last update.                       |

*/